/**
 * notification-tabs.ts — Chia hộp thông báo thành tab theo LOẠI thông báo của hệ thống (hàm thuần, có test).
 * Loại do backend ghi ở cột `type`; loại lạ / mới chưa liệt kê rơi vào tab "Hệ thống" (không bao giờ mất khỏi "Tất cả").
 */
import { i18n } from '@/lib/i18n'

export type NotiTabKey = 'all' | 'message' | 'assign' | 'appointment' | 'billing' | 'system'

interface TabDef { key: NotiTabKey; label: string; types?: string[] }

export const NOTI_TABS: TabDef[] = [
  { key: 'all', get label() { return i18n.t('sharedMisc:notificationTabs.tabs.all') } },
  { key: 'message', get label() { return i18n.t('sharedMisc:notificationTabs.tabs.message') }, types: ['message', 'unreplied', 'internal_mention'] },
  { key: 'assign', get label() { return i18n.t('sharedMisc:notificationTabs.tabs.assign') }, types: ['assignment', 'reclaim', 'handoff'] },
  { key: 'appointment', get label() { return i18n.t('sharedMisc:notificationTabs.tabs.appointment') }, types: ['appointment'] },
  { key: 'billing', get label() { return i18n.t('sharedMisc:notificationTabs.tabs.billing') }, types: ['billing', 'billing_paid'] },
  // "Hệ thống" = mọi loại còn lại (automation, connectivity, feedback, affiliate, contact, loại mới…).
  { key: 'system', get label() { return i18n.t('sharedMisc:notificationTabs.tabs.system') } },
]

const LISTED = NOTI_TABS.flatMap((t) => t.types ?? [])

export function tabOfType(type: string): Exclude<NotiTabKey, 'all'> {
  return (NOTI_TABS.find((t) => t.types?.includes(type))?.key ?? 'system') as Exclude<NotiTabKey, 'all'>
}

/** Tham số lọc gửi server (`types` / `exclude`); tab "Tất cả" → không lọc. */
export function tabFilter(key: NotiTabKey): { types?: string; exclude?: string } {
  if (key === 'all') return {}
  if (key === 'system') return { exclude: LISTED.join(',') }
  return { types: NOTI_TABS.find((t) => t.key === key)?.types?.join(',') }
}

/** Số chưa đọc của một tab, từ bảng `unreadByType` server trả về. */
export function tabUnread(key: NotiTabKey, byType: Record<string, number> | undefined): number {
  if (!byType) return 0
  return Object.entries(byType).reduce((sum, [type, n]) => sum + (key === 'all' || tabOfType(type) === key ? n : 0), 0)
}

const TYPE_LABEL_KEYS: Record<string, string> = {
  message: 'message',
  unreplied: 'unreplied',
  internal_mention: 'internalMention',
  contact: 'contact',
  appointment: 'appointment',
  automation: 'automation',
  connectivity: 'connectivity',
  assignment: 'assignment',
  handoff: 'handoff',
  reclaim: 'reclaim',
  feedback: 'feedback',
  affiliate: 'affiliate',
  billing: 'billing',
  billing_paid: 'billingPaid',
}

/** Nhãn hiển thị của từng loại (nhãn nhỏ dưới mỗi thông báo). Loại lạ → trả lại chính mã loại. */
export function typeLabel(type: string): string {
  const key = TYPE_LABEL_KEYS[type]
  return key ? i18n.t(`sharedMisc:notificationTabs.types.${key}`) : type
}
